"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.assignStudentsToTeams = assignStudentsToTeams;
exports.convertSimulationTeamToMissionTeam = convertSimulationTeamToMissionTeam;
exports.getTeams = getTeams;
var _functions = require("../utility/functions");
var _heap = require("../utility/heap");
var _lodash = require("lodash");
/**
 * assignStudentsToTeams - assign students not already on valid current teams to other teams on the mission. If allowStealingStudentsFromCurrentTeams is false, then the students in valid (teams which still exist in the simulation) currentTeams will
 * all stay in their currently assigned teams.
 *
 * @param  {string[]} studentsToAssign - the students to assign to teams
 * @param  {SimulationTeam[]} simulationTeams - the teams on the simulation
 * @param  {Team[]} currentTeams - the current teams, will keep all students in currentTeams in their current team and placement (assuming that the team has a corresponding simulationTeams - if not the students in that team will be redistributed)
 * @param  {boolean} allowStealingStudentsFromCurrentTeams - if truthy, will allow stealing students from the current teams if needed to create new teams. False will not allow stealing to take place.
 *
 * @returns Team
 */
function assignStudentsToTeams(studentsToAssign, simulationTeams, currentTeams, allowStealingStudentsFromCurrentTeams) {
  const simulationTeamMap = {};
  simulationTeams.forEach(team => simulationTeamMap[team._id] = convertSimulationTeamToMissionTeam(team));
  // copy over teacher teams
  const teamsToCopyOver = currentTeams.filter(team => team.isTeacherTeam);
  const studentsToAssignTeamsFor = new Set(studentsToAssign);
  teamsToCopyOver.forEach(team => {
    if (team.lead) {
      studentsToAssignTeamsFor.delete(team.lead);
    }
    team.students.forEach(studentId => studentsToAssignTeamsFor.delete(studentId));
    delete simulationTeamMap[team.id];
  });
  const teamsWhichCanBeAssignedTo = currentTeams.filter(team => !team.isTeacherTeam);
  const teamsWithCounts = getTeams(studentsToAssignTeamsFor.size, simulationTeams, teamsWhichCanBeAssignedTo, allowStealingStudentsFromCurrentTeams);
  const reservedTeamMap = (0, _functions.toIdMap)(teamsWhichCanBeAssignedTo);
  teamsWithCounts.forEach(({
    teamId,
    count: targetTeamSize
  }) => {
    // carry over the student placement from the currentTeams to the new teams
    const team = simulationTeamMap[teamId];
    const reservedTeam = reservedTeamMap[teamId];
    if (!reservedTeam) {
      return;
    }
    let studentsPlacedInCurrentTeam = 0;
    const reservedTeamLead = reservedTeam.lead;
    if (reservedTeamLead && studentsPlacedInCurrentTeam !== targetTeamSize) {
      studentsToAssignTeamsFor.delete(reservedTeamLead);
      team.lead = reservedTeamLead;
      studentsPlacedInCurrentTeam += 1;
    }
    reservedTeam.students.forEach(studentId => {
      if (studentsPlacedInCurrentTeam === targetTeamSize) {
        return;
      }
      studentsToAssignTeamsFor.delete(studentId);
      team.students.push(studentId);
      studentsPlacedInCurrentTeam += 1;
    });
  });
  const studentsNotPlaced = (0, _lodash.shuffle)(Array.from(studentsToAssignTeamsFor));
  teamsWithCounts.forEach(({
    teamId,
    count
  }) => {
    const team = simulationTeamMap[teamId];
    const studentsToAdd = Math.max(count - getMemberCount(team), 0);
    if (!studentsToAdd) {
      return;
    }
    const studentsToAssignToTeam = studentsNotPlaced.splice(0, studentsToAdd);
    if (studentsToAssignToTeam.length && !team.lead) {
      team.lead = studentsToAssignToTeam.pop();
    }
    team.students.push(...studentsToAssignToTeam);
  });
  return [...Object.keys(simulationTeamMap).map(teamId => simulationTeamMap[teamId]), ...teamsToCopyOver];
}

/**
 * getTeams - get a list of teamIds along with their count of students to satisfy placing all studentCount students into simulation teams
 *
 * @param  {number} studentCount - the count of the students to place into teams
 * @param  {SimulationTeam[]} simulationTeams - the teams from the simulation
 * @param  {Team[]} currentTeams? - if truthy, will only increase the number of students in these teams such that the students in these teams can stay in their teams
 * @param  {boolean} allowStealingStudentsFromCurrentTeams - if truthy, will allow stealing students from the current teams if needed to create new teams. False will not allow stealing to take place.
 *
 * @returns { teamId: string, count: number }[] - a list of the number of students to place into each team
 */
function getTeams(studentCount, simulationTeams, currentTeams = [], allowStealingStudentsFromCurrentTeams = true) {
  let availableForNewTeams = {};
  const requiredTeams = simulationTeams.filter(({
    required
  }) => required);
  const unrequiredTeams = simulationTeams.filter(({
    required
  }) => !required);
  let placedStudents = 0;

  // initialize all teams
  simulationTeams.forEach(team => {
    availableForNewTeams[team._id] ??= {
      teamId: team._id,
      required: team.required,
      count: 0,
      startingStudentCount: 0,
      minStudents: team.minStudents,
      maxStudents: team.maxStudents
    };
  });
  currentTeams.forEach(team => {
    if (!availableForNewTeams[team.id]) {
      // the team is no longer valid for the simulation, allow their students to be reassigned
      return;
    }
    const studentCount = getMemberCount(team);
    placedStudents += studentCount;
    availableForNewTeams[team.id].count += studentCount;
    availableForNewTeams[team.id].startingStudentCount += studentCount;
  });

  // place in required teams
  if (requiredTeams.length) {
    const lowestCountTeams = (0, _heap.generateMinHeap)(requiredTeams.filter(({
      _id
    }) => {
      const currentTeamData = availableForNewTeams[_id];
      return currentTeamData && currentTeamData.count < currentTeamData.maxStudents;
    }).map(({
      _id
    }) => _id), (team1Id, team2Id) => availableForNewTeams[team1Id].count - availableForNewTeams[team2Id].count);
    for (const currentTeamId of (0, _heap.heapIterator)(lowestCountTeams)) {
      if (placedStudents >= studentCount) {
        break;
      }
      const currentTeam = availableForNewTeams[currentTeamId];
      currentTeam.count += 1;
      placedStudents += 1;
      if (currentTeam.count < currentTeam.maxStudents) {
        lowestCountTeams.push(currentTeamId);
      }
    }
  }

  // determine which un-required teams are currently in use
  const existingUnRequiredTeams = [];
  const availableUnRequiredTeamsToCreate = [];
  unrequiredTeams.forEach(team => {
    if (availableForNewTeams[team._id].count > 0) {
      existingUnRequiredTeams.push(team);
      return;
    }
    availableUnRequiredTeamsToCreate.push(team);
  });

  // fill un-required teams currently in use
  if (placedStudents < studentCount && existingUnRequiredTeams.length) {
    // fill current un-required teams to minStudents
    const teamsNotYetWithMinStudents = (0, _heap.generateMinHeap)(existingUnRequiredTeams.filter(({
      _id
    }) => {
      const currentTeamData = availableForNewTeams[_id];
      return currentTeamData && currentTeamData.count < currentTeamData.minStudents;
    }).map(({
      _id
    }) => _id), (team1Id, team2Id) => availableForNewTeams[team1Id].count - availableForNewTeams[team2Id].count);
    let nextTeamIdToAddStudentTo = null;
    while (placedStudents < studentCount && (nextTeamIdToAddStudentTo = teamsNotYetWithMinStudents.pop())) {
      if (nextTeamIdToAddStudentTo == null) {
        // this is checked above in the while loop condition, but flow does not recognize it
        break;
      }
      const currentTeamData = availableForNewTeams[nextTeamIdToAddStudentTo];
      currentTeamData.count += 1;
      placedStudents += 1;
      if (currentTeamData.count < currentTeamData.minStudents) {
        // re-add team to heap
        teamsNotYetWithMinStudents.push(nextTeamIdToAddStudentTo);
      }
    }

    // fill teams to maxStudent count
    const teamsNotYetWithMaxStudents = (0, _heap.generateMinHeap)(existingUnRequiredTeams.filter(({
      _id
    }) => {
      const currentTeamData = availableForNewTeams[_id];
      return currentTeamData && currentTeamData.count < currentTeamData.maxStudents;
    }).map(({
      _id
    }) => _id), (team1Id, team2Id) => availableForNewTeams[team1Id].count - availableForNewTeams[team2Id].count);
    while (placedStudents < studentCount && (nextTeamIdToAddStudentTo = teamsNotYetWithMaxStudents.pop())) {
      if (nextTeamIdToAddStudentTo == null) {
        // this is checked above in the while loop condition, but flow does not recognize it
        break;
      }
      const currentTeamData = availableForNewTeams[nextTeamIdToAddStudentTo];
      currentTeamData.count += 1;
      placedStudents += 1;
      if (currentTeamData.count < currentTeamData.maxStudents) {
        // re-add team to heap
        teamsNotYetWithMaxStudents.push(nextTeamIdToAddStudentTo);
      }
    }
  }

  // create un-required teams if necessary
  while (placedStudents < studentCount && availableUnRequiredTeamsToCreate.length) {
    const currentTeam = availableUnRequiredTeamsToCreate.splice(0, 1)[0];
    let studentsToPlaceInTeam = Math.min(studentCount - placedStudents, currentTeam.maxStudents);
    if (studentsToPlaceInTeam < currentTeam.minStudents) {
      let slackCalculator = getSlack;
      if (!allowStealingStudentsFromCurrentTeams) {
        slackCalculator = getAddedStudentSlack;
      }
      let totalPossibleStealableStudents = 0;
      Object.keys(availableForNewTeams).forEach(teamId => {
        totalPossibleStealableStudents += Math.max(slackCalculator(availableForNewTeams[teamId]), 0);
      });
      if (totalPossibleStealableStudents + studentsToPlaceInTeam < currentTeam.minStudents) {
        // even if all stealable students are moved to the new team, the new team's member count would not reach minStudents. Skip creating this team and any other team to respect priority.
        break; // if this is continue, it will try to create the next team in the list
      }
      const teamsWithMaxSlackStudents = (0, _heap.generateMinHeap)(Object.keys(availableForNewTeams).filter(teamId => slackCalculator(availableForNewTeams[teamId]) > 0), (team1Id, team2Id) => {
        // prefer taking from un-required teams
        const team1 = availableForNewTeams[team1Id];
        const team2 = availableForNewTeams[team2Id];
        if (team1.required === team2.required) {
          // prefer taking student from teams with higher slack
          return slackCalculator(team2) - slackCalculator(team1);
        }

        // prefer taking from un-required teams
        if (team1.required) {
          return 1;
        }
        return -1;
      });
      let nextTeamIdToTakeStudentsFrom = null;
      while (studentsToPlaceInTeam < currentTeam.minStudents && (nextTeamIdToTakeStudentsFrom = teamsWithMaxSlackStudents.pop())) {
        if (nextTeamIdToTakeStudentsFrom == null) {
          // this is checked above in the while loop condition, but flow does not recognize it
          break;
        }
        const teamToTakeStudentFrom = availableForNewTeams[nextTeamIdToTakeStudentsFrom];
        studentsToPlaceInTeam += 1;
        teamToTakeStudentFrom.count -= 1;
        placedStudents -= 1;
        if (slackCalculator(teamToTakeStudentFrom) > 0) {
          // can take more students from this team if needed
          teamsWithMaxSlackStudents.push(nextTeamIdToTakeStudentsFrom);
        }
      }
    }
    placedStudents += studentsToPlaceInTeam;
    availableForNewTeams[currentTeam._id].count += studentsToPlaceInTeam;
  }

  // fill above max as required
  if (placedStudents < studentCount) {
    let teamsToOverfill = Object.keys(availableForNewTeams).filter(teamId =>
    // only overfill already created teams
    availableForNewTeams[teamId].count > 0);
    if (!teamsToOverfill.length) {
      // no good option here, allow creating new teams if necessary
      teamsToOverfill = Object.keys(availableForNewTeams);
    }
    const nextTeamToPlaceRemainingStudents = (0, _heap.generateMinHeap)(teamsToOverfill, (team1Id, team2Id) => {
      const team1 = availableForNewTeams[team1Id];
      const team2 = availableForNewTeams[team2Id];
      const team1Priority = team1.count / team1.maxStudents;
      const team2Priority = team2.count / team2.maxStudents;
      return team1Priority - team2Priority;
    });
    for (const nextTeamId of (0, _heap.heapIterator)(nextTeamToPlaceRemainingStudents)) {
      if (placedStudents >= studentCount) {
        break;
      }
      availableForNewTeams[nextTeamId].count += 1;
      placedStudents += 1;
      nextTeamToPlaceRemainingStudents.push(nextTeamId);
    }
  }
  return Object.keys(availableForNewTeams).map(teamId => ({
    teamId,
    count: availableForNewTeams[teamId].count
  }));
}

/**
 * getAddedStudentSlack - get the amount of students which can be removed before removing students the team started with or violating the teams minStudent count
 *
 * @param  {{count:number, startingStudentCount: number, minStudents: number}} team - the team to get the slack count for
 *
 * @returns number
 */
function getAddedStudentSlack(team) {
  return team.count - Math.max(team.minStudents, team.startingStudentCount);
}

/**
 * getSlack - get the difference between the current student count and the min number of students in a team
 *
 * @param  {{count:number, minStudents: number}} team - the team to get the slack count for
 *
 * @returns number
 */
function getSlack(team) {
  return team.count - team.minStudents;
}

/**
 * getMemberCount - get the number of students on the team
 *
 * @param  {Team} team - the team to get the number of students for
 */
function getMemberCount(team) {
  return team.students.length + (team.lead && 1 || 0);
}

/**
 * convertSimulationTeamToMissionTeam - get a mission team from the simulation team
 *
 * @param  {SimulationTeam} simulationTeam - a simulation team
 *
 * @returns Team
 */
function convertSimulationTeamToMissionTeam(simulationTeam) {
  return {
    displayName: simulationTeam.name,
    id: simulationTeam._id,
    lead: null,
    maxStudents: simulationTeam.maxStudents,
    minStudents: simulationTeam.minStudents,
    students: [],
    type: simulationTeam.engineeringPanelComponentType,
    icon: simulationTeam.icon,
    isTeacherTeam: false,
    isRequired: simulationTeam.required
  };
}